import React from "react";
import {
  ClipboardList,
  Gavel,
  Inbox,
  Handshake,
  Truck,
} from "lucide-react";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Submit Request",
    desc: "Define your product, volume and quality needs in a simple form.",
  },
  {
    icon: Gavel,
    title: "Create Bid Request",
    desc: "Set schedule, invite suppliers, and launch a live reverse auction.",
  },
  {
    icon: Inbox,
    title: "Receive Offers",
    desc: "Compare competitive offers from verified global manufacturers.",
  },
  {
    icon: Handshake,
    title: "Agreement & Payment",
    desc: "Sign the contract and process escrowed payment securely.",
  },
  {
    icon: Truck,
    title: "Production & Delivery",
    desc: "Track production and shipping until the goods reach your door.",
  },
];

export default function ProcessFlow() {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative py-20 md:py-28 bg-gradient-to-b from-white to-slate-50"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="w-full max-w-5xl mx-auto text-center reveal mb-14 md:mb-20 px-1 overflow-x-auto overflow-y-hidden [scrollbar-width:thin]">
          <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3">
            How it works
          </p>
          <h2
            data-testid="process-title"
            className="font-display font-black text-3xl sm:text-4xl md:text-6xl leading-tight tracking-tight whitespace-nowrap inline-block"
          >
            <span className="text-slate-900">Commodity</span>
            <span className="text-red-600">Bid</span>{" "}
            <span className="text-[#2563eb]">
              Process{"\u00A0"}Flow
            </span>
          </h2>
        </div>

        {/* Desktop: horizontal stepper */}
        <div className="hidden lg:block relative">
          <div className="absolute top-10 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-red-300" />
          <div className="grid grid-cols-5 gap-6 relative">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`process-step-${i}`}
                  className="reveal text-center"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="relative mx-auto w-20 h-20 rounded-full bg-white border-2 border-blue-100 shadow-lg flex items-center justify-center group hover:border-blue-500 transition-colors">
                    <Icon
                      size={28}
                      className="text-blue-600 group-hover:scale-110 transition-transform"
                      strokeWidth={2}
                    />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 text-white text-xs font-black flex items-center justify-center shadow-md">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display font-extrabold text-base text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-xs text-slate-600 leading-relaxed max-w-[180px] mx-auto">
                    {s.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile/Tablet: vertical stepper */}
        <div className="lg:hidden space-y-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`process-step-mobile-${i}`}
                className="reveal flex gap-4 items-start"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="relative w-14 h-14 rounded-full bg-white border-2 border-blue-100 shadow flex items-center justify-center">
                    <Icon size={22} className="text-blue-600" strokeWidth={2} />
                    <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-black flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="w-0.5 h-10 bg-blue-200 mt-1" />
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <h3 className="font-display font-extrabold text-base text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
